import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, Calendar as CalendarIcon, Clock as ClockIcon, Users as UsersIcon, AlertTriangle, UserPlus } from "lucide-react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { Session } from "../features/sessions/models/Session";
import { Invitee } from "../features/sessions/models/Invitee";
import { InviteeType } from "../features/sessions/models/InviteeType";
import { PaymentActivityType } from "../features/sessions/models/PaymentActivityType";
import { BookingStatus } from "../features/sessions/models/BookingStatus";
import { RecurrenceType } from "../features/sessions/models/RecurrenceType";
import {
  mapFrequencyToRecurrenceType,
  mapRecurrenceTypeToFrequency,
} from "../features/sessions/utils/RecurrenceTypeMapper";
import {
  deriveSessionTitle,
  generateTitleFromInvitees,
} from "../features/sessions/utils/SessionTitle";
import {
  prefillDate,
  readNewSessionPrefill,
} from "../features/sessions/utils/sessionPrefill";
import { fetchSchedulePreferences } from "../features/instructor-preferences/slice-extensions/instructor-preferences-slice-extension";
import CalendarInvitesShortcut from "../features/instructor-preferences/CalendarInvitesShortcut";
import {
  selectInstructorPreferences,
  selectPreferencesInitializationStatus,
} from "../features/instructor-preferences/instructor-preferences-slice";
import {
  getWindowForDate,
  isTimeWithinWorkingHours,
  getDefaultSessionStart,
} from "../features/instructor-preferences/utils/workingHoursUtils";
import {
  fetchSessionById,
  createSession,
  updateSession,
  addInviteeToSession,
  removeInviteeFromSession,
} from "../features/sessions/slice-extensions/sessions-slice-extension";
import {
  setCurrentSession,
  clearCurrentSession,
  selectCurrentSession,
} from "../features/sessions/sessions-slice";
import SessionBookingModeField from "../components/sessions/SessionBookingModeField";
import { useSingleDateMemberCount } from "../features/sessions/hooks/useSingleDateMemberCount";
import SessionCancellationPolicyField from "../components/sessions/SessionCancellationPolicyField";
import SessionTopicPlanField from "../components/training/SessionTopicPlanField";
import { saveTopicPlanDraft } from "../features/training/slice-extensions/training-topics-slice-extension";
import {
  fetchMembers,
  fetchGroups,
  createMember,
} from "../features/members/slice-extensions/members-slice-extension";
import {
  selectMembers,
  selectGroups,
} from "../features/members/members-slice";
import { ROUTES } from "../routes";
import Breadcrumbs from "../components/Breadcrumbs";
import Input from "../components/input/Input";
import Textarea from "../components/textarea/Textarea";
import Button from "../components/button/Button";
import DatePicker from "../components/date-picker/DatePicker";
import TimeSelector from "../components/time-selector/TimeSelector";
import DurationSelector from "../components/duration-selector/DurationSelector";
import FrequencySelector from "../components/frequency-selector/FrequencySelector";
import RecurrenceOptions, {
  RecurrenceConfig,
} from "../components/frequency-selector/RecurrenceOptions";
import SessionConflictNotice from "../components/session-conflicts/SessionConflictNotice";
import InstructorSelect from "../features/instructors/components/InstructorSelect";
import EditScopeSwitch, { SessionEditScope } from "../components/occurrences/EditScopeSwitch";
import EditSingleOccurrencePanel from "../components/occurrences/EditSingleOccurrencePanel";
import LeftMenu from "../components/LeftMenu";
import ConfirmationModal from "../components/ConfirmationModal";
import UnsavedChangesGuard from "../components/unsaved-changes/UnsavedChangesGuard";
import {
  validateSession,
  FormErrors,
  showValidationErrorNotification,
} from "../utils/validation";
import {
  showSuccessNotification,
  showErrorNotification,
  SESSION_NOTIFICATIONS,
} from "../utils/notifications";
import { extractTypedApiErrorMessage } from "../api/utils/errorHandling";
import { formatWeekdayDate } from "../features/utils/displayFormat";
import { getDisplayLocale } from "../features/utils/timezoneUtils";

/** Hash a string to a positive integer */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
};

const MEMBER_COLORS = [
  "#367D7D", "#2E6B6B", "#3D8F8F", "#2A7A5E",
  "#4A8C6F", "#3B7A9E", "#2D6E8A", "#458B74",
];
const GROUP_COLORS = [
  "#6317CF", "#462673", "#6C0DF2", "#8833FF",
  "#9966FF", "#993300", "#C2570A", "#0F8A80",
];

const getMemberColor = (firstName: string, lastName: string): string =>
  MEMBER_COLORS[hashString(firstName + lastName) % MEMBER_COLORS.length];

const getGroupColor = (name: string): string =>
  GROUP_COLORS[hashString(name) % GROUP_COLORS.length];

const getInitials = (firstName: string, lastName: string): string =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const getGroupAbbreviation = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  return name.trim().substring(0, 2).toUpperCase();
};

const CreateEditSession = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const currentSession = useAppSelector(selectCurrentSession);
  // Tracks only the initial session fetch on mount. We deliberately don't reuse
  // the slice's global `status` here: invitee add/remove thunks also flip it to
  // Loading, which would unmount the form mid-edit and cause a visible flicker.
  const [isInitialFetching, setIsInitialFetching] = useState(false);

  const allMembers = useAppSelector(selectMembers);
  const allGroups = useAppSelector(selectGroups);
  const preferences = useAppSelector(selectInstructorPreferences);
  const preferencesInitStatus = useAppSelector(selectPreferencesInitializationStatus);

  const [errors, setErrors] = useState<FormErrors>({});
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [originalSession, setOriginalSession] = useState<Session | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [participantSearchText, setParticipantSearchText] = useState("");
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
  const [highlightedParticipantIndex, setHighlightedParticipantIndex] = useState(-1);
  const [isCreatingMember, setIsCreatingMember] = useState(false);
  const [showOutsideHoursConfirmation, setShowOutsideHoursConfirmation] = useState(false);
  const pendingSubmitSessionRef = useRef<Session | null>(null);
  const titleManuallyEdited = useRef(false);
  // Set right before an intentional leave (post-save redirect, confirmed
  // discard) so the unsaved-changes guard doesn't double-prompt.
  const leaveApprovedRef = useRef(false);

  // Determine if we're in edit mode based on URL parameter
  const isEditMode = Boolean(id);

  // Recurring sessions edit either the entire series (the regular form) or a
  // single occurrence; a list row passes its date so the latter pre-selects it.
  const [editScope, setEditScope] = useState<SessionEditScope>("series");
  const navOccurrenceDate = (location.state as { occurrenceDate?: string } | null)
    ?.occurrenceDate;
  // A caller can seed the new-session form with a day (calendar cell) and a
  // time slot (available-slots list); anything omitted keeps the default.
  const prefill = useMemo(() => readNewSessionPrefill(location.state), [location.state]);
  const isRecurringEdit =
    isEditMode &&
    !!currentSession &&
    currentSession.id !== "" &&
    currentSession.recurrenceType !== RecurrenceType.No;

  // Initialize session for create mode or load for edit mode
  useEffect(() => {
    const initializeSession = async () => {
      if (isEditMode && id) {
        setIsInitialFetching(true);
        try {
          // Fetch session using Redux action
          const result = await dispatch(fetchSessionById(id));
          if (fetchSessionById.fulfilled.match(result)) {
            const session = result.payload as Session;
            // Store original session for change detection (deep copy as plain strings)
            setOriginalSession({ ...session });
            titleManuallyEdited.current = true;
          } else {
            showErrorNotification(SESSION_NOTIFICATIONS.LOAD_ERROR);
            navigate(ROUTES.SESSIONS);
            return;
          }
        } catch (error) {
          showErrorNotification(SESSION_NOTIFICATIONS.LOAD_ERROR);
          navigate(ROUTES.SESSIONS);
        } finally {
          setIsInitialFetching(false);
        }
      } else {
        // Create mode: clear any stale session now; the working-hours-aware default
        // is seeded once preferences resolve (see the effect below) so the start
        // time can default to the next in-working-hours slot.
        dispatch(clearCurrentSession());
        setOriginalSession(null);
      }
    };

    initializeSession();
  }, [isEditMode, id, dispatch, navigate]);

  // Seed the new-session default once preferences have resolved (success or
  // failure). Defaults the start to the next in-working-hours slot — e.g. today
  // from now if hours remain, otherwise the next working day's start (tomorrow
  // 09:00) — instead of "today, after hours".
  //
  // currentSession is shared Redux state, and any late-resolving fetchSessionById
  // (details/edit page left moments earlier) overwrites it with an *existing*
  // session. A create-mode draft always has id === "" — anything else is that
  // stale leak, so the defaults are (re)applied whenever it shows up.
  useEffect(() => {
    if (isEditMode) return;
    if (preferencesInitStatus !== "initialized") return;
    if (currentSession && currentSession.id === "") return;

    const now = new Date();
    const preferred = getDefaultSessionStart(preferences, now);
    const defaultDate = preferred
      ? preferred.date
      : now.getHours() >= 23 && now.getMinutes() >= 30
        ? new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
        : new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // A caller-supplied day / time wins over the working-hours default.
    const date = prefillDate(prefill) ?? defaultDate;
    const iso = date.toISOString();
    const defaultSession: Session = {
      id: "",
      paymentActivityType: PaymentActivityType.Regular,
      title: "",
      description: undefined,
      dateStart: iso,
      timeLocal: prefill?.startTime ?? (preferred ? preferred.time : ""),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      duration: prefill?.duration ?? 0,
      isAllDay: false,
      status: BookingStatus.Active,
      recurrenceType: mapFrequencyToRecurrenceType("none"),
      invitees: [],
      exceptions: [],
      dateCreated: iso,
      dateModified: iso,
    };
    dispatch(setCurrentSession(defaultSession));
    setOriginalSession({ ...defaultSession });
  }, [isEditMode, preferencesInitStatus, preferences, dispatch, currentSession, prefill]);

  // Clear errors when session changes (but not during submission to prevent scroll issues)
  useEffect(() => {
    if (currentSession && !isSubmitting) {
      setErrors({});
    }
  }, [currentSession, isSubmitting]);

  // Fetch members and groups for participant picker
  useEffect(() => {
    dispatch(fetchMembers());
    dispatch(fetchGroups());
  }, [dispatch]);

  // Fetch instructor's preferences (default duration + working hours).
  useEffect(() => {
    if (preferencesInitStatus === "not-initialized") {
      dispatch(fetchSchedulePreferences());
    }
  }, [dispatch, preferencesInitStatus]);

  // Working-hours window for the currently selected date (null = day is non-working).
  // `undefined` until preferences load — TimeSelector then behaves as before.
  const workingHoursWindow = useMemo(() => {
    if (!preferences || !currentSession?.dateStart) return undefined;
    const window = getWindowForDate(preferences, currentSession.dateStart);
    return window ? { start: window.start, end: window.end } : null;
  }, [preferences, currentSession?.dateStart]);

  const isOutsideWorkingHours = useMemo(() => {
    if (!preferences || !currentSession?.dateStart || !currentSession?.timeLocal || !currentSession?.duration) {
      return false;
    }
    const time = currentSession.timeLocal.includes(":")
      ? currentSession.timeLocal.split(":").slice(0, 2).join(":")
      : currentSession.timeLocal;
    return !isTimeWithinWorkingHours(preferences, currentSession.dateStart, time, currentSession.duration);
  }, [preferences, currentSession?.dateStart, currentSession?.timeLocal, currentSession?.duration]);

  // Resolved participant data for display. A Group-type invitee whose invitedUserId
  // is a member id (per-member row, Type=Group only records provenance) resolves as
  // that member rather than a group.
  const resolvedParticipants = useMemo(() => {
    if (!currentSession) return [];
    const memberMap = new Map(allMembers.map((m) => [m.id, m]));
    const groupMap = new Map(allGroups.map((g) => [g.id, g]));
    return currentSession.invitees
      .filter((inv) => inv.type !== InviteeType.OneOff)
      .map((inv) => {
        const group = inv.type === InviteeType.Group ? groupMap.get(inv.invitedUserId) : undefined;
        const member = group ? undefined : memberMap.get(inv.invitedUserId);
        return { invitee: inv, member, group };
      });
  }, [currentSession?.invitees, allMembers, allGroups]);

  // Members with an upcoming Confirmed one-date seat who are not series guests.
  const singleDateCount = useSingleDateMemberCount(isEditMode ? currentSession : null, allMembers, allGroups);

  // Filtered members + groups for the search dropdown
  const filteredParticipants = useMemo(() => {
    const selectedIds = new Set(currentSession?.invitees.map((i) => i.invitedUserId) || []);
    const search = participantSearchText.trim().toLowerCase();

    const matchingGroups = allGroups
      .filter((g) => !selectedIds.has(g.id))
      .filter((g) => !search || g.name.toLowerCase().includes(search));

    const matchingMembers = allMembers
      .filter((m) => !selectedIds.has(m.id))
      .filter((m) => {
        if (!search) return true;
        const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
        return fullName.includes(search) || (m.email?.toLowerCase().includes(search) ?? false);
      });

    return { groups: matchingGroups, members: matchingMembers };
  }, [allGroups, allMembers, currentSession?.invitees, participantSearchText]);

  // Flat, ordered list of selectable dropdown options (groups, then members, then
  // the optional "create" row) — the single source of truth for keyboard navigation.
  const participantOptions = useMemo(() => {
    const options: (
      | { kind: "group"; id: string }
      | { kind: "member"; id: string }
      | { kind: "create" }
    )[] = [
      ...filteredParticipants.groups.map((g) => ({ kind: "group" as const, id: g.id })),
      ...filteredParticipants.members.map((m) => ({ kind: "member" as const, id: m.id })),
    ];
    if (participantSearchText.trim().length > 0) {
      options.push({ kind: "create" });
    }
    return options;
  }, [filteredParticipants, participantSearchText]);

  const activateParticipantOption = (option: (typeof participantOptions)[number]) => {
    if (option.kind === "group") {
      handleAddParticipant(option.id, InviteeType.Group);
    } else if (option.kind === "member") {
      handleAddParticipant(option.id, InviteeType.Individual);
    } else {
      handleCreateAndAddMember();
    }
  };

  const handleParticipantSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (participantOptions.length === 0) return;
      setShowParticipantDropdown(true);
      setHighlightedParticipantIndex((prev) => (prev + 1) % participantOptions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (participantOptions.length === 0) return;
      setShowParticipantDropdown(true);
      setHighlightedParticipantIndex((prev) =>
        prev <= 0 ? participantOptions.length - 1 : prev - 1
      );
    } else if (e.key === "Enter") {
      // Only act when an item is highlighted, so Enter doesn't hijack form submission otherwise.
      if (showParticipantDropdown && highlightedParticipantIndex >= 0 &&
          highlightedParticipantIndex < participantOptions.length) {
        e.preventDefault();
        activateParticipantOption(participantOptions[highlightedParticipantIndex]);
      }
    } else if (e.key === "Escape") {
      setShowParticipantDropdown(false);
      setHighlightedParticipantIndex(-1);
    }
  };

  const getGroupMemberCount = (groupId: string): number =>
    allMembers.filter((m) => m.groups.some((g) => g.id === groupId)).length;

  const handleAddParticipant = async (entityId: string, type: InviteeType) => {
    if (isEditMode && currentSession?.id) {
      // Edit mode: call API directly
      const typeNum = type === InviteeType.Group ? 0 : 1;
      const result = await dispatch(
        addInviteeToSession({ sessionId: currentSession.id, invitedUserId: entityId, type: typeNum })
      );
      if (addInviteeToSession.fulfilled.match(result)) {
        // The thunk returns the updated session — update Redux
        dispatch(setCurrentSession(result.payload));
      }
    } else if (currentSession) {
      // Create mode: add to local invitees array
      const newInvitee: Invitee = {
        id: "",
        invitedUserId: entityId,
        bookingId: "",
        type,
        dateCreated: "",
        dateModified: "",
      };
      const updatedInvitees = [...currentSession.invitees, newInvitee];
      const updatedSession: Session = {
        ...currentSession,
        invitees: updatedInvitees,
      };
      if (!titleManuallyEdited.current) {
        updatedSession.title = generateTitleFromInvitees(updatedInvitees, allMembers, allGroups);
      }
      dispatch(setCurrentSession(updatedSession));
    }
    setParticipantSearchText("");
    setShowParticipantDropdown(false);
  };

  // Splits a typed name into first/last: with two+ words the first word is the
  // first name and the rest the last name; a single word fills both (last name
  // is required server-side and can be corrected on the member page later).
  const splitMemberName = (raw: string): { firstName: string; lastName: string } => {
    const parts = raw.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: "", lastName: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  };

  const handleCreateAndAddMember = async () => {
    const { firstName, lastName } = splitMemberName(participantSearchText);
    if (!firstName) return;

    setIsCreatingMember(true);
    try {
      const result = await dispatch(
        createMember({
          // Quick-create from the session form — the API assigns the creator.
          instructorId: null,
          firstName,
          lastName,
          // No explicit choice: the API stores the organization's language.
          primaryLanguageCode: null,
          timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneUtcOffsetHours: new Date().getTimezoneOffset() / -60,
          email: null,
          notes: null,
          address: null,
          dateOfBirth: null,
          guardianMemberId: null,
          tags: [],
        })
      );

      if (createMember.fulfilled.match(result)) {
        await handleAddParticipant(result.payload.id, InviteeType.Individual);
        showSuccessNotification(t("sessions.memberCreated", { name: `${firstName} ${lastName}`.trim() }));
      } else {
        showErrorNotification(extractTypedApiErrorMessage(result.payload, t("sessions.memberCreateError")));
      }
    } finally {
      setIsCreatingMember(false);
    }
  };

  const handleRemoveParticipant = async (invitee: Invitee) => {
    if (isEditMode && currentSession?.id && invitee.id) {
      // Edit mode: call API directly
      const result = await dispatch(
        removeInviteeFromSession({ sessionId: currentSession.id, inviteeId: invitee.id })
      );
      if (removeInviteeFromSession.fulfilled.match(result)) {
        dispatch(setCurrentSession(result.payload));
      }
    } else if (currentSession) {
      // Create mode: remove from local invitees array
      const updatedInvitees = currentSession.invitees.filter((i) => i.invitedUserId !== invitee.invitedUserId);
      const updatedSession: Session = {
        ...currentSession,
        invitees: updatedInvitees,
      };
      if (!titleManuallyEdited.current) {
        updatedSession.title = generateTitleFromInvitees(updatedInvitees, allMembers, allGroups);
      }
      dispatch(setCurrentSession(updatedSession));
    }
  };

  // Helper function to check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!currentSession || !originalSession) {
      return false;
    }

    // Compare relevant fields that user can modify
    const titleChanged =
      (currentSession.title || "") !== (originalSession.title || "");
    const descriptionChanged =
      (currentSession.description || "") !==
      (originalSession.description || "");

    // Handle date comparison more safely
    let dateChanged = false;
    try {
      const currentDate = new Date(currentSession.dateStart);
      const originalDate = new Date(originalSession.dateStart);
      if (!isNaN(currentDate.getTime()) && !isNaN(originalDate.getTime())) {
        dateChanged = currentDate.getTime() !== originalDate.getTime();
      }
    } catch (error) {
      dateChanged = false;
    }

    const timeChanged =
      (currentSession.timeLocal || "") !== (originalSession.timeLocal || "");
    const durationChanged =
      (currentSession.duration || 0) !== (originalSession.duration || 0);
    const recurrenceChanged =
      currentSession.recurrenceType !== originalSession.recurrenceType ||
      (currentSession.recurrenceDays ?? []).join(",") !==
        (originalSession.recurrenceDays ?? []).join(",") ||
      currentSession.recurrenceDaysNumber !== originalSession.recurrenceDaysNumber ||
      (currentSession.recurrenceDateEnd?.getTime() ?? null) !==
        (originalSession.recurrenceDateEnd?.getTime() ?? null) ||
      currentSession.finishAfterOccurrences !== originalSession.finishAfterOccurrences;

    const currentInviteeIds = new Set(currentSession.invitees.map((i) => i.invitedUserId));
    const originalInviteeIds = new Set(originalSession.invitees.map((i) => i.invitedUserId));
    const inviteesChanged =
      currentInviteeIds.size !== originalInviteeIds.size ||
      [...currentInviteeIds].some((id) => !originalInviteeIds.has(id));

    return (
      titleChanged ||
      descriptionChanged ||
      dateChanged ||
      timeChanged ||
      durationChanged ||
      recurrenceChanged ||
      inviteesChanged
    );
  };

  // Helper function to navigate back to previous page
  const navigateToPreviousPage = () => {
    // Check if we have a referrer in location state
    const referrer = location.state?.from;
    if (referrer) {
      navigate(referrer);
      return;
    }

    // Try to go back in browser history if available
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    // Fallback: If in edit mode, go to session details; otherwise go to sessions list
    const fallbackDestination =
      isEditMode && currentSession?.id
        ? ROUTES.SESSION_DETAILS.replace(":id", currentSession.id)
        : ROUTES.SESSIONS;

    navigate(fallbackDestination);
  };

  const handleInputChange =
    (field: "title" | "description") =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      if (field === "title") {
        titleManuallyEdited.current = newValue.length > 0;
      }

      if (currentSession) {
        const updatedSession: Session = {
          ...currentSession,
          [field]: field === "description" ? newValue || undefined : newValue,
        };
        dispatch(setCurrentSession(updatedSession));
      }

      // Clear error when user starts typing
      const errorField = field === "title" ? "eventName" : "eventNotes";
      if (errors[errorField]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorField];
          return newErrors;
        });
      }
    };

  const handleDateSelect = (date: Date) => {
    if (currentSession) {
      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);
      const updatedSession: Session = {
        ...currentSession,
        dateStart: normalizedDate.toISOString(),
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear date error when user selects a date
    if (errors.eventDate) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.eventDate;
        return newErrors;
      });
    }
  };

  const handleTimeSelect = (time: string) => {
    if (currentSession) {
      const updatedSession: Session = {
        ...currentSession,
        timeLocal: time,
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear time error when user selects a time
    if (errors.startTime) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.startTime;
        return newErrors;
      });
    }
  };

  const handleDurationSelect = (duration: string) => {
    if (currentSession) {
      const durationNum = parseInt(duration, 10);
      const updatedSession: Session = {
        ...currentSession,
        duration: isNaN(durationNum) ? 0 : durationNum,
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear duration error when user selects a duration
    if (errors.duration) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.duration;
        return newErrors;
      });
    }
  };

  const handleFrequencySelect = (frequency: string) => {
    if (currentSession) {
      // Re-selecting the current frequency keeps the configured details intact.
      if (mapRecurrenceTypeToFrequency(currentSession.recurrenceType) === frequency) {
        return;
      }

      const recurrenceType = mapFrequencyToRecurrenceType(frequency);
      const startDay = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][
        new Date(currentSession.dateStart).getDay()
      ];
      const usesDays =
        recurrenceType === RecurrenceType.Weekly ||
        recurrenceType === RecurrenceType.BiWeekly;
      const updatedSession: Session = {
        ...currentSession,
        recurrenceType,
        recurrenceDays: usesDays ? [startDay] : undefined,
        recurrenceDaysNumber:
          recurrenceType === RecurrenceType.MonthlyDayNumber
            ? new Date(currentSession.dateStart).getDate()
            : undefined,
        recurrenceDateEnd: undefined,
        finishAfterOccurrences: undefined,
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear frequency error when user selects a frequency
    if (errors.frequency) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.frequency;
        return newErrors;
      });
    }
  };

  const handleRecurrenceConfigChange = (config: RecurrenceConfig) => {
    if (!currentSession) return;
    dispatch(
      setCurrentSession({
        ...currentSession,
        recurrenceType: config.recurrenceType,
        recurrenceDays: config.recurrenceDays,
        recurrenceDaysNumber: config.recurrenceDaysNumber,
        recurrenceDateEnd: config.recurrenceDateEnd,
        finishAfterOccurrences: config.finishAfterOccurrences,
      })
    );
  };

  const performSubmit = async (sessionForSubmit: Session, scrollPosition: number) => {
    try {
      let result;

      if (isEditMode && sessionForSubmit.id) {
        // Update existing session using currentSession from Redux
        result = await dispatch(
          updateSession({
            id: sessionForSubmit.id,
            session: sessionForSubmit,
          })
        );
      } else {
        // Create new session using currentSession from Redux
        result = await dispatch(createSession(sessionForSubmit));
      }

      if (
        createSession.fulfilled.match(result) ||
        updateSession.fulfilled.match(result)
      ) {
        // The topic plan saves against the saved id; on failure the instructor stays to see why.
        const savedId = result.payload.id ?? "";
        const planSave = await dispatch(
          saveTopicPlanDraft({ sessionId: savedId, oneOff: result.payload.recurrenceType === RecurrenceType.No })
        );
        if (saveTopicPlanDraft.rejected.match(planSave)) {
          setIsSubmitting(false);
          showErrorNotification(t("training.topicPlan.saveFailed"));
          if (!isEditMode && savedId) {
            // A created session must not be created again: continue on its edit page.
            leaveApprovedRef.current = true;
            navigate(ROUTES.EDIT_SESSION.replace(":id", savedId), { replace: true });
          }
          return;
        }

        // Restore scroll position to prevent jumping to top
        window.scrollTo(0, scrollPosition);

        const successMessage = isEditMode
          ? t("sessionForm.updatedToast")
          : t("sessionForm.createdToast");
        showSuccessNotification(successMessage, 3000);
        setTimeout(() => {
          setIsSubmitting(false);
          leaveApprovedRef.current = true;

          let navigateRoute: string;

          if (isEditMode) {
            // For edit mode: Check if we have a referrer in location state for proper redirect
            const referrer = location.state?.from;
            if (referrer) {
              // Navigate back to the page that initiated the edit
              navigateRoute = referrer;
            } else {
              // Fallback: go to session details
              navigateRoute = ROUTES.SESSION_DETAILS.replace(
                ":id",
                currentSession?.id || ""
              );
            }
          } else {
            // For create mode: Always navigate to sessions list
            navigateRoute = ROUTES.SESSIONS;
          }

          navigate(navigateRoute, {
            replace: false,
            preventScrollReset: true,
          });
        }, 1000);
      } else {
        // Handle API validation errors or other failures
        const defaultErrorMessage = isEditMode
          ? SESSION_NOTIFICATIONS.UPDATE_ERROR
          : SESSION_NOTIFICATIONS.CREATE_ERROR;
        const errorMessage = extractTypedApiErrorMessage(
          result.payload as any,
          defaultErrorMessage
        );
        setIsSubmitting(false);
        showErrorNotification(errorMessage);
      }
    } catch (error) {
      console.error("Error saving session:", error);
      const errorMessage = isEditMode
        ? SESSION_NOTIFICATIONS.UPDATE_ERROR
        : SESSION_NOTIFICATIONS.CREATE_ERROR;
      setIsSubmitting(false);
      showErrorNotification(errorMessage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    const scrollPosition = window.pageYOffset;
    setIsSubmitting(true);
    setErrors({});

    // Auto-fill blank title from invitees (or date fallback) before validating.
    let sessionForSubmit = currentSession;
    if (sessionForSubmit && !sessionForSubmit.title?.trim()) {
      const derivedTitle = deriveSessionTitle(
        sessionForSubmit.invitees,
        allMembers,
        allGroups,
        sessionForSubmit.dateStart,
        sessionForSubmit.timeLocal,
        sessionForSubmit.timeZone,
      );
      sessionForSubmit = { ...sessionForSubmit, title: derivedTitle };
      dispatch(setCurrentSession(sessionForSubmit));
    }

    const newErrors = validateSession(sessionForSubmit, isEditMode, t);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      showValidationErrorNotification(newErrors, t);
      scrollToFirstError(newErrors);
      return;
    }

    if (!sessionForSubmit) {
      const errorMessage = isEditMode
        ? SESSION_NOTIFICATIONS.UPDATE_ERROR
        : SESSION_NOTIFICATIONS.CREATE_ERROR;
      setIsSubmitting(false);
      showErrorNotification(errorMessage);
      return;
    }

    // If the session is outside the instructor's working hours, ask for confirmation
    // before submitting. The modal's "Confirm" handler picks up the staged session.
    if (preferences && isOutsideWorkingHours) {
      pendingSubmitSessionRef.current = sessionForSubmit;
      setShowOutsideHoursConfirmation(true);
      return;
    }

    await performSubmit(sessionForSubmit, scrollPosition);
  };

  const handleConfirmOutsideHours = async () => {
    const staged = pendingSubmitSessionRef.current;
    pendingSubmitSessionRef.current = null;
    setShowOutsideHoursConfirmation(false);
    if (!staged) {
      setIsSubmitting(false);
      return;
    }
    await performSubmit(staged, window.pageYOffset);
  };

  const handleCancelOutsideHours = () => {
    pendingSubmitSessionRef.current = null;
    setShowOutsideHoursConfirmation(false);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    // In edit mode, always show confirmation to be safe
    // In create mode, check for changes
    if (isEditMode || hasUnsavedChanges()) {
      setShowCancelConfirmation(true);
    } else {
      // No changes, navigate directly
      setErrors({});
      navigateToPreviousPage();
    }
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirmation(false);
    setErrors({});
    leaveApprovedRef.current = true;
    navigateToPreviousPage();
  };

  const handleCancelCancel = () => {
    setShowCancelConfirmation(false);
  };

  const scrollToFirstError = (errors: FormErrors): void => {
    setTimeout(() => {
      const firstErrorField = Object.keys(errors)[0];
      let targetElement: Element | null = null;
      let elementId = "";

      switch (firstErrorField) {
        case "eventName":
          elementId = "event_name";
          break;
        case "eventDate":
          // For date picker, scroll to the date picker container
          targetElement =
            document.querySelector('[data-testid="date-picker"]') ||
            document.querySelector(".date-picker") ||
            document.querySelector('input[type="date"]');
          break;
        case "startTime":
          // For time selector, scroll to time selector container with multiple fallbacks
          targetElement =
            document.querySelector('[data-testid="time-selector"]') ||
            document.querySelector(".start-time-tabs") ||
            document.querySelector(".time-selector") ||
            document.querySelector('input[type="time"]');
          break;
        case "duration":
          // For duration selector, scroll to duration container
          targetElement =
            document.querySelector('[data-testid="duration-selector"]') ||
            document.querySelector(".duration-selector");
          break;
        case "frequency":
          // For frequency selector, scroll to frequency container
          targetElement =
            document.querySelector('[data-testid="frequency-selector"]') ||
            document.querySelector(".frequency-selector");
          break;
        case "eventNotes":
          elementId = "session_notes";
          break;
      }

      // Try to find element by ID if not found by selector
      if (!targetElement && elementId) {
        targetElement = document.getElementById(elementId);
      }

      if (targetElement) {
        // Calculate smooth scroll position with proper offset for better UX
        const elementRect = targetElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;

        // Instead of centering, position the element near the top with reasonable padding
        // This prevents over-scrolling and ensures the error message is visible
        const headerOffset = 100; // Account for any fixed headers
        const targetScrollPosition = Math.max(
          0,
          absoluteElementTop - headerOffset
        );

        // Use smooth scrolling with gentle positioning
        window.scrollTo({
          top: targetScrollPosition,
          behavior: "smooth",
        });

        // Focus the element if it's focusable (after scroll completes)
        setTimeout(() => {
          if (
            targetElement instanceof HTMLElement &&
            (targetElement.tagName === "INPUT" ||
              targetElement.tagName === "TEXTAREA" ||
              targetElement.tagName === "SELECT" ||
              targetElement.hasAttribute("tabindex"))
          ) {
            targetElement.focus();
          }
        }, 500);
      }
    }, 150);
  };

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side — extra bottom padding on phones clears the fixed action bar */}
        <div className="w-full min-w-0 p-4 pb-28 lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1200px]">
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h1>{isEditMode ? t("sessions.editSession") : t("sessions.createSession")}</h1>
                <div className="flex flex-row items-center gap-3 w-full lg:w-auto">
                  <Link
                    to={ROUTES.SESSIONS}
                    className="btn btn-secondary flex-1 lg:flex-none"
                  >
                    <ChevronLeft size={18} strokeWidth={2} />
                    <span>{t("common.backToList")}</span>
                  </Link>
                </div>
              </div>

              {isRecurringEdit && (
                <EditScopeSwitch scope={editScope} onChange={setEditScope} />
              )}

              {isRecurringEdit && editScope === "occurrence" && currentSession && (
                <EditSingleOccurrencePanel
                  session={currentSession}
                  initialDate={navOccurrenceDate}
                  onSaved={navigateToPreviousPage}
                />
              )}

              {/* Only show form when not fetching or when in create mode */}
              {(!isInitialFetching || !isEditMode) && editScope === "series" && (
                <form
                  onSubmit={handleSubmit}
                  className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8"
                >
                  {/* Left column — main form fields */}
                  <div className="space-y-6 lg:space-y-4">
                  {/* Schedule Details */}
                  <div className="space-y-6 lg:space-y-5">
                    <h3>{t("sessions.scheduleDetails")}</h3>

                    <Input
                      label={t("sessions.titleLabel")}
                      optional
                      helperText={t("sessions.titleOptionalHint")}
                      type="text"
                      name="event_name"
                      id="event_name"
                      placeholder={t("sessions.titlePlaceholder")}
                      value={currentSession?.title || ""}
                      onChange={handleInputChange("title")}
                      error={errors.eventName}
                      disabled={isSubmitting}
                    />

                    {/* Instructor assignment — visible only to Owner/Admin in a
                        multi-instructor organization. */}
                    <InstructorSelect
                      value={currentSession?.instructorId}
                      onChange={(instructorId) => {
                        if (currentSession) {
                          dispatch(
                            setCurrentSession({ ...currentSession, instructorId })
                          );
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Session Date */}
                  <DatePicker
                    selectedDate={
                      currentSession?.dateStart
                        ? new Date(currentSession.dateStart)
                        : undefined
                    }
                    onDateSelect={handleDateSelect}
                    disabled={isSubmitting}
                  />

                  {/* Error message for date selection */}
                  {errors.eventDate && (
                    <p className="field-error mt-1">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
                          fill="currentColor"
                        />
                      </svg>
                      {errors.eventDate}
                    </p>
                  )}

                  {/* Start Time */}
                  <TimeSelector
                    selectedTime={
                      currentSession?.timeLocal
                        ? currentSession.timeLocal.includes(":")
                          ? currentSession.timeLocal
                              .split(":")
                              .slice(0, 2)
                              .join(":")
                          : currentSession.timeLocal
                        : ""
                    }
                    onTimeSelect={handleTimeSelect}
                    error={errors.startTime}
                    disabled={isSubmitting}
                    selectedDate={
                      currentSession?.dateStart
                        ? new Date(currentSession.dateStart)
                        : undefined
                    }
                    workingHoursWindow={workingHoursWindow}
                  />

                  {isOutsideWorkingHours && (
                    <p className="mt-1 text-sm text-secondary-30 dark:text-secondary-80">
                      <Trans
                        i18nKey="sessionForm.outsideWorkingHoursNotice"
                        defaults="This session is outside your working hours. You can change them in <settingsLink>Settings</settingsLink>."
                        components={{
                          settingsLink: <a href={ROUTES.SETTINGS} className="underline" />,
                        }}
                      />
                    </p>
                  )}

                  {/* Duration */}
                  <DurationSelector
                    selectedDuration={
                      currentSession?.duration
                        ? currentSession.duration.toString()
                        : ""
                    }
                    defaultDuration={
                      preferences ? String(preferences.defaultDurationMinutes) : undefined
                    }
                    onDurationSelect={handleDurationSelect}
                    error={errors.duration}
                    disabled={isSubmitting}
                  />

                  {/* Capacity — optional max roster size; empty = unlimited */}
                  <label className="block text-sm font-medium text-grey-30 dark:text-grey-70">
                    {t("sessionForm.capacityLabel", "Capacity (optional)")}
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      className="input mt-1 w-40"
                      value={currentSession?.capacity ?? ""}
                      disabled={isSubmitting}
                      placeholder={t("sessionForm.capacityPlaceholder", "Unlimited")}
                      onChange={(event) => {
                        if (!currentSession) return;
                        const raw = event.target.value;
                        const parsed = parseInt(raw, 10);
                        dispatch(
                          setCurrentSession({
                            ...currentSession,
                            capacity: raw === "" || isNaN(parsed) ? null : Math.max(1, parsed),
                          }),
                        );
                      }}
                    />
                    <span className="mt-1 block text-xs font-normal text-grey-40 dark:text-grey-80">
                      {t(
                        "sessionForm.capacityHint",
                        "When full, new members go to the waitlist on the session page.",
                      )}
                    </span>
                  </label>
                  <SessionBookingModeField disabled={isSubmitting} />
                  <SessionCancellationPolicyField disabled={isSubmitting} />
                  <SessionTopicPlanField disabled={isSubmitting} />

                  {/* Frequency */}
                  <FrequencySelector
                    selectedFrequency={
                      currentSession
                        ? mapRecurrenceTypeToFrequency(
                            currentSession.recurrenceType
                          )
                        : "none"
                    }
                    onFrequencySelect={handleFrequencySelect}
                    isDisabled={isSubmitting}
                  />

                  {/* Recurrence details (days, cadence, series end) */}
                  {currentSession &&
                    currentSession.recurrenceType !== RecurrenceType.No && (
                      <RecurrenceOptions
                        config={{
                          recurrenceType: currentSession.recurrenceType,
                          recurrenceDays: currentSession.recurrenceDays,
                          recurrenceDaysNumber: currentSession.recurrenceDaysNumber,
                          recurrenceDateEnd: currentSession.recurrenceDateEnd,
                          finishAfterOccurrences: currentSession.finishAfterOccurrences,
                        }}
                        onChange={handleRecurrenceConfigChange}
                      />
                    )}

                  {/* Advisory overlap check against the instructor's other sessions */}
                  {currentSession && (
                    <SessionConflictNotice
                      session={currentSession}
                      excludeSessionId={isEditMode ? currentSession.id : undefined}
                    />
                  )}

                  {/* Error message for frequency selection */}
                  {errors.frequency && (
                    <p className="field-error mt-1">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
                          fill="currentColor"
                        />
                      </svg>
                      {errors.frequency}
                    </p>
                  )}

                  {/* Divider */}
                  <hr className="border-grey-70" />

                  {/* Notes — primary content, stays in the left column */}
                  <div className="space-y-6 lg:space-y-5">
                    <h3>{t("sessions.sessionNotes")}</h3>

                    <Textarea
                      label={t("sessions.notesLabel")}
                      name="session_notes"
                      id="session_notes"
                      placeholder={t("sessions.notesPlaceholder")}
                      value={currentSession?.description || ""}
                      onChange={handleInputChange("description")}
                      error={errors.eventNotes}
                      disabled={isSubmitting}
                    />
                  </div>
                  </div>
                  {/* End left column */}

                  {/* Right column — sticky sidebar: live summary + participants + actions */}
                  <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
                    {/* Live summary card */}
                    <div className="with-border rounded-2xl p-4 lg:p-5 space-y-3">
                      <div className="flex items-center gap-2">
                        <CalendarIcon size={18} strokeWidth={2} className="text-primary-50 dark:text-primary-80" />
                        <h3 className="text-base font-semibold">
                          {t("sessionForm.summaryTitle", "Summary")}
                        </h3>
                      </div>
                      <p className="text-base font-medium text-grey-20 dark:text-white line-clamp-2">
                        {currentSession?.title?.trim() || (
                          <span className="italic text-grey-50 dark:text-grey-70">
                            {t("sessionForm.untitledSession", "Untitled session")}
                          </span>
                        )}
                      </p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2 text-grey-30 dark:text-grey-90">
                          <CalendarIcon size={14} strokeWidth={2} className="shrink-0" />
                          <span>
                            {currentSession?.dateStart
                              ? formatWeekdayDate(currentSession.dateStart, getDisplayLocale())
                              : t("sessionForm.noDateSelected", "No date selected")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-grey-30 dark:text-grey-90">
                          <ClockIcon size={14} strokeWidth={2} className="shrink-0" />
                          <span>
                            {currentSession?.timeLocal && currentSession?.duration
                              ? `${currentSession.timeLocal.split(":").slice(0, 2).join(":")} · ${currentSession.duration} min`
                              : currentSession?.timeLocal
                                ? currentSession.timeLocal.split(":").slice(0, 2).join(":")
                                : t("sessionForm.noTimeSelected", "No time selected")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-grey-30 dark:text-grey-90">
                          <UsersIcon size={14} strokeWidth={2} className="shrink-0" />
                          <span>
                            {resolvedParticipants.length
                              ? t("sessions.participantsCount", { count: resolvedParticipants.length })
                              : t("sessionForm.noParticipants", "No participants")}
                          </span>
                        </div>
                      </div>
                      {isOutsideWorkingHours && (
                        <div className="flex items-start gap-2 rounded-lg bg-secondary-95 dark:bg-secondary-20 px-3 py-2 text-sm text-secondary-30 dark:text-secondary-80">
                          <AlertTriangle size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
                          <span>{t("sessionForm.outsideWorkingHoursBadge", "Outside your working hours")}</span>
                        </div>
                      )}
                      <CalendarInvitesShortcut />
                    </div>

                    {/* Participants */}
                    <div className="with-border rounded-2xl p-4 lg:p-5 space-y-4">
                      <div>
                        <h3 className="text-base font-semibold">
                          {t("sessions.participantsCount", {
                            count: resolvedParticipants.length,
                          })}
                        </h3>
                        {resolvedParticipants.length === 0 && (
                          <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
                            {t("sessions.noParticipantsYet")}
                          </p>
                        )}
                        {singleDateCount > 0 && (
                          <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
                            {t("sessions.singleDateBookings", { count: singleDateCount })}
                          </p>
                        )}
                      </div>

                      {/* Selected participants list */}
                      {resolvedParticipants.length > 0 && (
                        <div className="overflow-hidden">
                          {resolvedParticipants.map((p) => {
                            // Per-member Group invitees resolve to a member — render as one.
                            const isGroup = p.invitee.type === InviteeType.Group && !p.member;
                            const name = isGroup
                              ? p.group?.name || "Unknown Group"
                              : p.member
                                ? `${p.member.firstName} ${p.member.lastName}`
                                : t("sessions.formerMember", "Former member");
                            const email = !isGroup ? p.member?.email : undefined;
                            const avatarColor = isGroup
                              ? getGroupColor(p.group?.name || "")
                              : getMemberColor(p.member?.firstName || "", p.member?.lastName || "");
                            const avatarText = isGroup
                              ? getGroupAbbreviation(p.group?.name || "")
                              : getInitials(p.member?.firstName || "", p.member?.lastName || "");
                            const memberCount = isGroup ? getGroupMemberCount(p.invitee.invitedUserId) : 0;

                            return (
                              <div
                                key={p.invitee.invitedUserId}
                                className="flex w-full items-center justify-between gap-3"
                              >
                                <div className="flex shrink items-center gap-3 py-2 min-w-0">
                                  <div
                                    className="relative flex size-8 shrink-0 items-center justify-center rounded-full"
                                    style={{ backgroundColor: avatarColor }}
                                  >
                                    <span className="text-xs font-bold text-white">
                                      {avatarText}
                                    </span>
                                    {isGroup && (
                                      <div className="notification-bottom border-white dark:border-primary-10">
                                        {memberCount}
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="line-clamp-1 text-sm">{name}</p>
                                    {email && (
                                      <p className="text-grey-50 dark:text-grey-70 line-clamp-1 max-w-fit text-xs break-all">
                                        {email}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-text dark:hover:text-primary-70 dark:text-white"
                                  onClick={() => handleRemoveParticipant(p.invitee)}
                                  disabled={isSubmitting}
                                >
                                  <span>{t("sessions.remove")}</span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Participant search */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-grey-30 dark:text-white mb-1">
                          {t("sessions.addParticipants")}
                        </label>
                        <input
                          type="text"
                          className="input"
                          placeholder={t("sessions.searchParticipantsPlaceholder")}
                          value={participantSearchText}
                          onChange={(e) => {
                            setParticipantSearchText(e.target.value);
                            setShowParticipantDropdown(true);
                            setHighlightedParticipantIndex(-1);
                          }}
                          onFocus={() => setShowParticipantDropdown(true)}
                          onKeyDown={handleParticipantSearchKeyDown}
                          disabled={isSubmitting}
                        />

                        {/* Dropdown */}
                        {showParticipantDropdown &&
                          (filteredParticipants.groups.length > 0 ||
                            filteredParticipants.members.length > 0 ||
                            participantSearchText.trim().length > 0) && (
                          <div className="absolute z-10 w-full mt-1 max-h-60 overflow-y-auto rounded-lg border border-grey-70 dark:border-primary-20 bg-white dark:bg-primary-20 shadow-lg">
                            {/* Groups */}
                            {filteredParticipants.groups.map((group, groupIndex) => {
                              const memberCount = getGroupMemberCount(group.id);
                              const optionIndex = groupIndex;
                              const isHighlighted = highlightedParticipantIndex === optionIndex;
                              return (
                                <button
                                  key={`group-${group.id}`}
                                  type="button"
                                  className={`flex items-center gap-3 w-full px-4 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors ${
                                    isHighlighted ? "bg-grey-95 dark:bg-primary-30" : ""
                                  }`}
                                  onMouseEnter={() => setHighlightedParticipantIndex(optionIndex)}
                                  onClick={() => handleAddParticipant(group.id, InviteeType.Group)}
                                >
                                  <div
                                    className="relative flex size-8 items-center justify-center rounded-full shrink-0"
                                    style={{ backgroundColor: getGroupColor(group.name) }}
                                  >
                                    <span className="text-xs font-bold text-white">
                                      {getGroupAbbreviation(group.name)}
                                    </span>
                                    <div className="notification-bottom">
                                      {memberCount}
                                    </div>
                                  </div>
                                  <p className="line-clamp-1 text-sm">{group.name}</p>
                                </button>
                              );
                            })}
                            {/* Members */}
                            {filteredParticipants.members.map((member, memberIndex) => {
                              const optionIndex = filteredParticipants.groups.length + memberIndex;
                              const isHighlighted = highlightedParticipantIndex === optionIndex;
                              return (
                              <button
                                key={`member-${member.id}`}
                                type="button"
                                className={`flex items-center gap-3 w-full px-4 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors ${
                                  isHighlighted ? "bg-grey-95 dark:bg-primary-30" : ""
                                }`}
                                onMouseEnter={() => setHighlightedParticipantIndex(optionIndex)}
                                onClick={() => handleAddParticipant(member.id, InviteeType.Individual)}
                              >
                                <div
                                  className="flex size-8 items-center justify-center rounded-full shrink-0"
                                  style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                                >
                                  <span className="text-xs font-bold text-white">
                                    {getInitials(member.firstName, member.lastName)}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="line-clamp-1 text-sm">
                                    {member.firstName} {member.lastName}
                                  </p>
                                  {member.email && (
                                    <p className="text-grey-50 dark:text-grey-70 line-clamp-1 text-xs break-all">
                                      {member.email}
                                    </p>
                                  )}
                                </div>
                              </button>
                              );
                            })}
                            {/* Create a new member from the typed name */}
                            {participantSearchText.trim().length > 0 && (() => {
                              const optionIndex =
                                filteredParticipants.groups.length + filteredParticipants.members.length;
                              const isHighlighted = highlightedParticipantIndex === optionIndex;
                              return (
                              <button
                                key="create-member"
                                type="button"
                                className={`flex items-center gap-3 w-full px-4 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors border-t border-grey-80 dark:border-primary-10 disabled:opacity-50 ${
                                  isHighlighted ? "bg-grey-95 dark:bg-primary-30" : ""
                                }`}
                                onMouseEnter={() => setHighlightedParticipantIndex(optionIndex)}
                                onClick={handleCreateAndAddMember}
                                disabled={isCreatingMember}
                              >
                                <div className="flex size-8 items-center justify-center rounded-full shrink-0 bg-grey-85 dark:bg-primary-30 text-primary-40 dark:text-primary-90">
                                  <UserPlus className="size-4" />
                                </div>
                                <p className="line-clamp-1 text-sm">
                                  {isCreatingMember
                                    ? t("sessions.creatingMember")
                                    : t("sessions.createMember", { name: participantSearchText.trim() })}
                                </p>
                              </button>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action bar — fixed to the viewport bottom on phones so
                        Create/Cancel are always reachable without scrolling past
                        the whole form; back in normal flow in the lg sidebar. */}
                    <div className="fixed inset-x-0 bottom-0 z-20 flex flex-row gap-3 border-t border-grey-90 bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-grey-50 dark:bg-primary-10 lg:static lg:z-auto lg:border-t-0 lg:bg-transparent lg:p-0 lg:pb-0 dark:lg:bg-transparent">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        className="flex-1 lg:flex-initial"
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                        className="flex-1 lg:flex-initial"
                      >
                        {isSubmitting
                          ? isEditMode
                            ? t("common.saving")
                            : t("common.creating")
                          : isEditMode
                          ? t("common.saveChanges")
                          : t("sessions.createSession")}
                      </Button>
                    </div>
                  </aside>
                </form>
              )}
              {isInitialFetching && isEditMode && (
                <div
                  className="flex items-center gap-2 text-grey-30 dark:text-grey-80"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="animate-spin rounded-full h-5 w-5 border-2 border-primary-40 dark:border-primary-90 border-t-transparent dark:border-t-transparent"
                    aria-hidden="true"
                  ></div>
                  <span>{t("sessions.sessionLoading")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Route-level guard: sidebar links, browser back and tab close */}
      <UnsavedChangesGuard
        when={() => !leaveApprovedRef.current && hasUnsavedChanges()}
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelConfirmation}
        title={t("sessions.discardChangesTitle")}
        message={
          isEditMode
            ? t("sessions.discardChangesEdit")
            : t("sessions.discardChangesCreate")
        }
        confirmText={t("common.discard")}
        cancelText={t("common.keepEditing")}
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelCancel}
        variant="danger"
      />

      {/* Outside-working-hours confirmation */}
      <ConfirmationModal
        isOpen={showOutsideHoursConfirmation}
        title={t("sessionForm.outsideHoursConfirmTitle", "Outside working hours")}
        message={t(
          "sessionForm.outsideHoursConfirmMessage",
          "This session falls outside the working hours you set in your preferences. Continue anyway?",
        )}
        confirmText={t("common.continue", "Continue")}
        cancelText={t("common.cancel")}
        onConfirm={handleConfirmOutsideHours}
        onCancel={handleCancelOutsideHours}
        variant="warning"
      />
    </section>
  );
};

export default CreateEditSession;
